import { datadogLogs } from "@datadog/browser-logs";
import type { ClientFetchOption } from "better-auth";

import { IS_CAPACITOR, IS_CHROME_EXTENSION, IS_IN_IFRAME } from "@src/env";

import { BEARER_TOKEN_KEY } from "./auth.constants";

/**
 * Central setter for the bearer token, mirroring {@link getBearerAuthToken}.
 * Every path that mints a token for the bearer-auth embeddings (chrome
 * extension, iframe, Capacitor) should write it through here rather than
 * touching `localStorage` directly.
 */
export function setBearerAuthToken(token: string): void {
  if (!token || !token.trim()) {
    datadogLogs.logger.error("Refused to store empty bearer token");
    return;
  }

  localStorage.setItem(BEARER_TOKEN_KEY, token);
}

function persistAuthTokenFromResponse(
  ctx: Parameters<NonNullable<ClientFetchOption["onSuccess"]>>[0],
) {
  const authToken = ctx.response.headers.get("set-auth-token");

  if (authToken) {
    setBearerAuthToken(authToken);
  }
}

export function usesBearerAuth(): boolean {
  return IS_CHROME_EXTENSION || IS_IN_IFRAME || IS_CAPACITOR;
}

export function getBearerAuthToken(): string | null {
  if (!usesBearerAuth()) {
    return null;
  }

  return localStorage.getItem(BEARER_TOKEN_KEY);
}

export async function getBearerAuthHeaders(): Promise<Record<
  string,
  string
> | null> {
  if (!usesBearerAuth()) {
    return {};
  }

  const token = getBearerAuthToken();

  if (token) {
    return { Authorization: `Bearer ${token}` };
  }

  return null;
}

const bearerFetchOptions: ClientFetchOption = {
  onSuccess: (ctx) => {
    persistAuthTokenFromResponse(ctx);
  },
  auth: {
    type: "Bearer",
    token: async () => {
      return localStorage.getItem(BEARER_TOKEN_KEY) || "";
    },
  },
};

const defaultFetchOptions: ClientFetchOption = {
  credentials: "include" as const,
};

export function getAuthClientFetchOptions(): ClientFetchOption {
  return usesBearerAuth() ? bearerFetchOptions : defaultFetchOptions;
}
